import { Injectable } from '@angular/core';
import { Client, IFrame, IMessage, StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { BehaviorSubject, filter, map, Observable, Subject, take, tap } from 'rxjs';
import { Apollo, gql } from 'apollo-angular';
import { environment } from '../../../environments/environment';
import {
  ChatOpenRequest,
  ChatUnread,
  MessageNotification,
  NotificationTask,
} from './message-notification.model';
import { OcrBatchJobDTO } from '../../models/ocr-batch/ocr-batch.model';
import {
  TradeEventFeedItem,
  TradeEventParty,
} from '../../models/trade-event/trade-event-feed.types';
import {
  MarkNotifTaskAsRead,
  MarkNotifTaskAsReplied,
  getUnreadNotificationsTask,
  getUnrepliedNotificationsTask,
} from '../../../app/graphql/graphql.queries_notif';


@Injectable({
  providedIn: 'root',
})
export class MessageNotificationService {
  private stompClient: Client | null = null;

  private isConnecting = false;
  private isConnected = false;

  private connected = new BehaviorSubject<boolean>(false);
  public connected$ = this.connected.asObservable();

  private messagesSubject = new BehaviorSubject<MessageNotification[]>([]);
  public messages$ = this.messagesSubject.asObservable();
  private chatMessagesSubject = new Subject<any>();
  public chatMessages$ = this.chatMessagesSubject.asObservable();
  
  private chatUnreadSubject = new BehaviorSubject<ChatUnread[]>([]);
  public chatUnread$ = this.chatUnreadSubject.asObservable();
  public chatUnreadCount$ = this.chatUnread$.pipe(
    map((items) => items.reduce((sum, i) => sum + (Number(i.unreadCount) || 0), 0)),
  );
  
  private chatOpenSubject = new BehaviorSubject<ChatOpenRequest | null>(null);
  public chatOpenRequest$ = this.chatOpenSubject.asObservable();

  private notificationsSubject = new BehaviorSubject<NotificationTask[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private static readonly GUARANTEE_DIRECT_TYPES = new Set([
    'GUARANTEE_DIRECT_ACCEPTED',
    'GUARANTEE_DIRECT_REFUSED',
    'AMENDMENT_DIRECT_ACCEPTED',
    'AMENDMENT_DIRECT_REFUSED',
  ]);
  /**
   * Notifications des demandes sur garantie (MT765) : des messages système rédigés pour l'utilisateur,
   * sans expéditeur. Affichées avec leur texte dans le centre de notifications, pas dans les messages.
   */
  private static readonly GUARANTEE_DEMAND_TYPES = new Set([
    'GUARANTEE_DEMAND_NOTIFIED',
    'GUARANTEE_DEMAND_REFUSED',
    'GUARANTEE_CLAIM_SETTLED',
    'GUARANTEE_REDUCTION_ADVISED',
    // Non-prorogation des garanties evergreen : notification de la banque (MT785).
    'GUARANTEE_NON_EXTENSION',
  ]);
  /**
   * Notifications d'un crédit import : son émission acceptée ou refusée par la banque, puis son cycle
   * documentaire (présentation conforme, réserves, refus, règlement). Messages système, sans expéditeur ni écran de réponse.
   */
  private static readonly CREDIT_PRESENTATION_TYPES = new Set([
    'CREDIT_DIRECT_ACCEPTED',
    'CREDIT_DIRECT_REFUSED',
    'CREDIT_DISCREPANCY_ADVISED',
    'CREDIT_REFUSAL_ADVISED',
    'CREDIT_SETTLEMENT_ADVISED',
    'CREDIT_CONFORMING_PRESENTATION_ADVISED',
    'CREDIT_DISCHARGE_ADVISED',
    'CREDIT_REIMBURSEMENT_ADVISED',
  ]);

  /** Les notifications de l'émission, qui ouvrent le crédit plutôt que l'historique de ses présentations. */
  static isCreditIssuanceType(type: string): boolean {
    return type === 'CREDIT_DIRECT_ACCEPTED' || type === 'CREDIT_DIRECT_REFUSED';
  }

  /** Types affichés dans le centre de notifications (cloche) et non dans la liste des messages. */
  static isBellType(type: string): boolean {
    return MessageNotificationService.GUARANTEE_DIRECT_TYPES.has(type)
      || MessageNotificationService.GUARANTEE_DEMAND_TYPES.has(type)
      || MessageNotificationService.CREDIT_PRESENTATION_TYPES.has(type);
  }

  public creditPresentationUnread$ = this.notifications$.pipe(
    map((list) => list.filter(
      (n) => !n.read && MessageNotificationService.CREDIT_PRESENTATION_TYPES.has(n.type)
    )),
  );

  public guaranteeDemandUnread$ = this.notifications$.pipe(
    map((list) => list.filter(
      (n) => !n.read && MessageNotificationService.GUARANTEE_DEMAND_TYPES.has(n.type)
    )),
  );

  public unread$ = this.notifications$.pipe(
    map((list) => list.filter(
      (n) => !n.read && !MessageNotificationService.isBellType(n.type)
    )),
  );

  public unreplied$ = this.notifications$.pipe(
    map((list) => list.filter((n) => !n.replied)),
  );

  public guaranteeDirectUnread$ = this.notifications$.pipe(
    map((list) => list.filter(
      (n) => !n.read && MessageNotificationService.GUARANTEE_DIRECT_TYPES.has(n.type)
    )),
  );

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private unrepliedCountSubject = new BehaviorSubject<number>(0);
  public unrepliedCount$ = this.unrepliedCountSubject.asObservable();

  private ocrBatchJobsSubject = new BehaviorSubject<OcrBatchJobDTO[]>([]);
  public ocrBatchJobs$ = this.ocrBatchJobsSubject.asObservable();

  /**
   * Trade events reçus en temps réel, un par frame. Flux d'événements et non état courant
   * (`Subject`, pas `BehaviorSubject`) : la liste affichée est construite par le pavé à partir du
   * rattrapage REST, ce canal ne fait que la compléter.
   */
  private tradeEventsSubject = new Subject<TradeEventFeedItem>();
  public tradeEvents$ = this.tradeEventsSubject.asObservable();

  /** Abonnement au topic de trade events, gardé pour ne pas le doubler et le libérer au logout. */
  private tradeEventsSubscription: StompSubscription | null = null;
  private subscribedTradeEventsTopic: string | null = null;

  constructor(
    private apollo: Apollo

  ) {}

  connect() {
    if (this.isConnecting || this.isConnected) {
      console.log('⚠️ WebSocket already connected or connecting');
      return;
    }

    if (this.stompClient) return;
    this.isConnecting = true;
    const sockJsOptions: any = {
      transports: ['websocket'],
      withCredentials: true,
    };
    const socket = new SockJS(`${environment.apiUrl}/chat`, null, {
      sockJsOptions,
    } as any);

    this.stompClient = new Client({
      webSocketFactory: () => socket as any,
      debug: (str: string) => {
        // Only log in development
        if (!environment.production) {
          console.log('STOMP:', str);
        }
      },
      onConnect: (frame: IFrame) => {
        this.isConnecting = false;
        this.isConnected = true;
        this.connected.next(true);
        console.log('✅ WebSocket connecté');
        this.loadUnreadNotifications();
        this.loadUnrepliedNotifications();
        this.loadChatUnread();
        this.subscribeToQueues();
      },
      onStompError: (frame: IFrame) => {
        console.error('❌ Erreur STOMP:', frame);
        this.isConnecting = false;
        this.isConnected = false;
      },
      onWebSocketClose: (evt: CloseEvent) => {
        console.log('🔌 WebSocket fermée:', evt);
        this.connected.next(false);
        this.isConnecting = false;
        this.isConnected = false;
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    this.stompClient.activate();
  }
  public isConnectedWebsocket(): boolean {
    return this.isConnected;
  }
  
  public publish(destination: string, body: unknown): boolean {
    if (!this.stompClient || !this.isConnected) return false;
    this.stompClient.publish({ destination, body: JSON.stringify(body) });
    return true;
  }
  private subscribeToQueues() {
    if (!this.stompClient) return;

    this.stompClient?.subscribe('/user/queue/chat', (message: IMessage) => {
      const msgBody = JSON.parse(message.body);
      this.messagesSubject.next(msgBody);
      this.chatMessagesSubject.next(msgBody);
    });

    this.stompClient.subscribe(
      '/user/queue/notification-message',
      (message: IMessage) => {
        const msgBody = JSON.parse(message.body);
        this.messagesSubject.next(msgBody);
        this.chatUnreadSubject.next(this.toChatUnreadList(msgBody));
      },
    );

    this.stompClient.subscribe('/user/queue/notifications/new', (msg) => {
      const notification = JSON.parse(msg.body) as NotificationTask;
      const current = this.notificationsSubject.value;
      const filtered = notification.taskId
        ? current.filter((n) => n.taskId !== notification.taskId)
        : current.filter((n) => n.id !== notification.id);
      const updated = [notification, ...filtered];
      this.notificationsSubject.next(updated);
      this.unreadCountSubject.next(updated.filter(
        (n) => !n.read && !MessageNotificationService.isBellType(n.type)
      ).length);
      this.unrepliedCountSubject.next(updated.filter((n) => !n.replied).length);
    });
    this.stompClient.subscribe('/user/queue/notifications/sync', (msg) => {
      const list: NotificationTask[] = JSON.parse(msg.body) || [];
      // Déduplication par taskId : garder la plus récente par tâche
      const seen = new Map<string, NotificationTask>();
      for (const n of list) {
        const key = n.taskId ? String(n.taskId) : `notask_${n.id}`;
        const existing = seen.get(key);
        if (!existing || Number(n.id) > Number(existing.id)) {
          seen.set(key, n);
        }
      }
      const deduped = Array.from(seen.values());
      this.notificationsSubject.next(deduped);
      this.unreadCountSubject.next(deduped.filter(
        (n) => !n.read && !MessageNotificationService.isBellType(n.type)
      ).length);
      this.unrepliedCountSubject.next(deduped.filter((n) => !n.replied).length);
    });

    this.stompClient.subscribe(
      '/user/queue/notification-read',
      (_message: IMessage) => {
        const current = this.notificationsSubject.value;
        const workflowUnreadCount = current.filter(
          (n) => !n.read && !MessageNotificationService.isBellType(n.type)
        ).length;
        this.unreadCountSubject.next(workflowUnreadCount);
      },
    );

    this.stompClient.subscribe(
      '/user/queue/notification-replied',
      (message: IMessage) => {
        const unrepliedCount = parseInt(message.body);

        this.unrepliedCountSubject.next(unrepliedCount);
      },
    );

    this.stompClient.subscribe('/user/queue/ocr-batch/update', (message: IMessage) => {
      const job = JSON.parse(message.body) as OcrBatchJobDTO;
      this.mergeOcrBatchJob(job);
    });

    this.stompClient.subscribe('/user/queue/ocr-batch/item-update', (message: IMessage) => {
      const payload = JSON.parse(message.body) as { jobId: number; item: OcrBatchJobDTO['items'][number] };
      const current = this.ocrBatchJobsSubject.value;
      const job = current.find(j => j.id === payload.jobId);
      if (!job) return;
      const updatedItems = job.items.map(it => it.id === payload.item.id ? payload.item : it);
      this.mergeOcrBatchJob({ ...job, items: updatedItems });
    });
  }

  /**
   * S'abonne aux trade events adressés à l'utilisateur : `/topic/trade-events/banking/{bic}` pour une
   * banque, `/topic/trade-events/corporate/{crn}` pour un corporate.
   *
   * La portée vient du serveur (`GET /internal/trade-events/recent`), pas d'une saisie : le backend
   * refuse de toute façon un SUBSCRIBE sur le topic d'un autre — et même sur le bon identifiant dans
   * le mauvais préfixe.
   *
   * Passe par le client STOMP unique de ce service, déjà connecté à `/chat` et piloté au
   * login/logout — ouvrir une seconde connexion pour un seul topic serait du gaspillage. Si la
   * connexion n'est pas encore établie (le pavé peut s'afficher avant), on attend le premier
   * `connected$` à `true` plutôt que de perdre l'abonnement.
   */
  public subscribeToTradeEvents(party: TradeEventParty, ref: string): void {
    if (!ref) return;

    const destination = this.tradeEventsTopic(party, ref);
    if (this.tradeEventsSubscription && this.subscribedTradeEventsTopic === destination) return;

    this.unsubscribeFromTradeEvents();
    this.subscribedTradeEventsTopic = destination;

    if (this.isConnected && this.stompClient) {
      this.doSubscribeToTradeEvents(destination);
      return;
    }
    this.connected$
      .pipe(
        filter((connected) => connected),
        take(1),
      )
      .subscribe(() => {
        if (this.subscribedTradeEventsTopic === destination) {
          this.doSubscribeToTradeEvents(destination);
        }
      });
  }

  private tradeEventsTopic(party: TradeEventParty, ref: string): string {
    return party === 'BANK'
      ? `/topic/trade-events/banking/${ref}`
      : `/topic/trade-events/corporate/${ref}`;
  }

  private doSubscribeToTradeEvents(destination: string): void {
    if (!this.stompClient) return;

    this.tradeEventsSubscription = this.stompClient.subscribe(
      destination,
      (message: IMessage) => {
        this.tradeEventsSubject.next(JSON.parse(message.body) as TradeEventFeedItem);
      },
    );
  }

  private unsubscribeFromTradeEvents(): void {
    try {
      this.tradeEventsSubscription?.unsubscribe();
    } catch {
    }
    this.tradeEventsSubscription = null;
    this.subscribedTradeEventsTopic = null;
  }

  private mergeOcrBatchJob(job: OcrBatchJobDTO): void {
    const current = this.ocrBatchJobsSubject.value;
    const updated = [job, ...current.filter(j => j.id !== job.id)]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 20);
    this.ocrBatchJobsSubject.next(updated);
  }

  public setOcrBatchJobsSnapshot(jobs: OcrBatchJobDTO[]): void {
    const current = this.ocrBatchJobsSubject.value;
    const byId = new Map(current.map(j => [j.id, j]));
    jobs.forEach(j => {
      if (!byId.has(j.id)) byId.set(j.id, j);
    });
    const merged = Array.from(byId.values())
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, 20);
    this.ocrBatchJobsSubject.next(merged);
  }

  /** Vide localement l'historique des lots OCR (après un "Clear all" côté serveur). */
  public clearOcrBatchJobs(): void {
    this.ocrBatchJobsSubject.next([]);
  }
  disconnect() {
  // Avant deactivate() : sinon la frame UNSUBSCRIBE partirait sur une socket déjà fermée.
  this.unsubscribeFromTradeEvents();

  if (this.stompClient) {
    this.stompClient.deactivate();
    this.stompClient = null;
  }

  this.isConnected = false;
  this.isConnecting = false;

  this.connected.next(false);

  this.messagesSubject.next([]);
  this.notificationsSubject.next([]);
  this.chatUnreadSubject.next([]);
  this.chatOpenSubject.next(null);

  this.unreadCountSubject.next(0);
  this.unrepliedCountSubject.next(0);
  this.ocrBatchJobsSubject.next([]);
}

  getMessageNotifications(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'network-only',
      query: gql`
        query getMessageNotifications {
          getMessageNotifications {
            senderId
            sender
            requestId
            dealId
            unreadCount
            timestamp
          }
        }
      `,
      variables: {},
    });
  }
  getUnreadNotifications(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'network-only',
      query: gql`
        query getMessageNotifications {
          getMessageNotifications {
            senderId
            sender
            requestId
            dealId
            unreadCount
            timestamp
          }
        }
      `,
      variables: {},
    });
  }
  getUnreadNotificationsTask(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'no-cache',
      query: getUnreadNotificationsTask,
    });
  }
  getUnrepliedNotificationTasks(): Observable<any> {
    return this.apollo.query({
      fetchPolicy: 'no-cache',
      query: getUnrepliedNotificationsTask,
    });
  }
  markNotificationsAsRead(notificationIds: string[]): Observable<any> {
    return this.apollo.mutate({
      mutation: MarkNotifTaskAsRead,
      variables: {
        notificationIds,
      },
    });
  }
  markAsRead(notificationId: string): Observable<any> {
    return this.markNotificationsAsRead([notificationId]).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;

        const updated = current.filter((n) => n.id !== notificationId);
        this.notificationsSubject.next(updated);
        this.unreadCountSubject.next(updated.filter(
          (n) => !n.read && !MessageNotificationService.isBellType(n.type)
        ).length);
      }),
    );
  }
  markNotificationsAsReplied(notificationIds: string[]): Observable<any> {
    return this.apollo.mutate({
      mutation: MarkNotifTaskAsReplied,
      variables: {
        notificationIds,
      },
    });
  }
  markAsReplied(notificationId: string): Observable<any> {
    return this.markNotificationsAsReplied([notificationId]).pipe(
      tap(() => {
        const current = this.notificationsSubject.value;

        const updated = current.filter((n) => n.id !== notificationId);
        this.notificationsSubject.next(updated);
        this.unrepliedCountSubject.next(updated.length);
      }),
    );
  }
  private mergeNotifications(newNotifs: NotificationTask[]) {
    const current = this.notificationsSubject.value || [];
    const allNotifs = [
      ...current,
      ...newNotifs.filter((n) => !current.some((c) => c.id === n.id)),
    ];
    // Déduplication par taskId : garder uniquement la notification la plus récente (id le plus grand) par tâche
    const seen = new Map<string, NotificationTask>();
    for (const n of allNotifs) {
      const key = n.taskId ? String(n.taskId) : `notask_${n.id}`;
      const existing = seen.get(key);
      if (!existing || Number(n.id) > Number(existing.id)) {
        seen.set(key, n);
      }
    }
    const deduped = Array.from(seen.values());
    this.notificationsSubject.next(deduped);
    this.unreadCountSubject.next(deduped.filter(
      (n) => !n.read && !MessageNotificationService.isBellType(n.type)
    ).length);
    this.unrepliedCountSubject.next(deduped.filter((n) => !n.replied).length);
  }
  loadChatUnread() {
    this.getMessageNotifications().subscribe({
      next: (result) => {
        this.chatUnreadSubject.next(
          this.toChatUnreadList(result?.data?.getMessageNotifications),
        );
      },
      error: (err) => {
        console.error('Error loading chat unread messages', err);
      },
    });
  }

  private toChatUnreadList(raw: unknown): ChatUnread[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((i) => i && Number(i.unreadCount) > 0)
      .map((i) => ({
        senderId: Number(i.senderId),
        sender: i.sender,
        requestId: i.requestId ?? null,
        dealId: i.dealId ?? null,
        unreadCount: Number(i.unreadCount),
      }));
  }
  
  requestChatOpen(item: Omit<ChatOpenRequest, 'at'>) {
    this.chatOpenSubject.next({ ...item, at: Date.now() });
  }
  
  consumeChatOpenRequest() {
    this.chatOpenSubject.next(null);
  }

  loadUnreadNotifications() {
    this.getUnreadNotificationsTask().subscribe({
      next: (result) => {
        const notifications = result.data?.unreadNotifications || [];
        this.mergeNotifications(notifications);
      },
      error: (err) => {
        console.error('Error loading unread notifications', err);
      },
    });
  }

  loadUnrepliedNotifications() {
    this.getUnrepliedNotificationTasks().subscribe({
      next: (result) => {
        const notifications = result.data?.unrepliedNotifications || [];
        this.mergeNotifications(notifications);
      },
      error: (err) => {
        console.error('Error loading unreplied notifications', err);
      },
    });
  }
}
